import React from 'react';

import { Button, Grid } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';

import { STATUSES_OPTIONS } from '@clearverify/constants';

import { PlatformStaffItemFragment, useUpdatePlatformStaffMutation } from 'src/api';
import { CheckboxField, SelectField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';

import { FormFields } from './constants';
import { FormValues } from './types';
import { formValidationSchema } from './validation';

interface PlatformStaffDetailModalContentFormProps {
  platformStaff: PlatformStaffItemFragment;
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const PlatformStaffDetailModalContentForm: React.FC<
  PlatformStaffDetailModalContentFormProps
> = ({ platformStaff, closeModal, switchClosability }) => {
  const { createApolloContext } = useNotification();

  const [updatePlatformStaff] = useUpdatePlatformStaffMutation({
    context: createApolloContext({
      successMessage: 'Clear Verify Staff updated successfully.',
      errorMessage: 'Failed to update Clear Verify Staff',
    }),
  });

  const handleSubmit = React.useCallback(
    async (formValues: FormValues) => {
      switchClosability(false);

      try {
        await updatePlatformStaff({
          variables: {
            where: { id: platformStaff.id },
            data: {
              isAbleToManagePlatformStaff: formValues.isAbleToManagePlatformStaff,
              user: {
                update: {
                  status: formValues.status,
                },
              },
            },
          },
        });

        switchClosability(true);
        closeModal();
      } catch {
        switchClosability(true);
      }
    },
    [switchClosability, closeModal, platformStaff.id, updatePlatformStaff],
  );

  const initialValues: FormValues = React.useMemo(
    () => ({
      [FormFields.status]: platformStaff.user.status,
      [FormFields.isAbleToManagePlatformStaff]: platformStaff.isAbleToManagePlatformStaff,
    }),
    [platformStaff.user.status, platformStaff.isAbleToManagePlatformStaff],
  );

  return (
    <Form<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={formValidationSchema}
      subscription={{ valid: true, submitting: true, dirty: true }}
      containerProps={{ mt: 2 }}
    >
      {({ submitting, valid, dirty }) => {
        return (
          <React.Fragment>
            <Grid size={12}>
              <Field
                label="Status"
                name={FormFields.status}
                component={SelectField}
                options={STATUSES_OPTIONS}
                disabled={submitting}
                fullWidth
              />
            </Grid>

            <Grid size={12}>
              <Field
                label="Is able to invite Clear Verify Staff"
                name={FormFields.isAbleToManagePlatformStaff}
                component={CheckboxField}
                disabled={submitting}
                fullWidth
              />
            </Grid>

            <Grid
              size={12}
              display="flex"
              alignContent="center"
              justifyContent="center"
              gap={2}
              mt={4}
            >
              <Button
                variant="outlined"
                color="error"
                onClick={() => closeModal()}
                disabled={submitting}
              >
                Close
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={!valid || submitting || !dirty}
                loading={submitting}
              >
                Save Changes
              </Button>
            </Grid>
          </React.Fragment>
        );
      }}
    </Form>
  );
};
